import {
    ChangeDetectionStrategy,
    Component,
    Input,
    ViewEncapsulation,
    inject,
    EventEmitter,
    Output,
    ElementRef,
    ViewChild,
    effect,
    computed,
    DestroyRef,
    signal,
    input,
    output,
} from '@angular/core';
import { IOnboardingSection, IOnboardingStep } from '../../fi-onboarding';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, AbstractControl, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { FiOnboardingBuilderHelperService } from '../../fi-onboarding-builder-helper.service';
import { FiOnboardingElementDropListHelperService } from '../fi-onboarding-element-drop-list-helper.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { IItemsListAction2 } from '@fi-app/shared/fi-items-list/fi-items-list';
import { FiItemsListOptionType } from '@fi-app/shared/fi-items-list/fi-items-list-option-type';
import { IFiOnboardingConfirmDeleteItemDialogResult } from '../fi-onboarding-confirm-delete-item-dialog/fi-onboarding-confirm-delete-item-dialog.component';
import { Animations } from '@fi-shared/utils/animations';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiOnboardingBuilderStoreService } from '../../fi-onboarding-builder-store.service';
import { ObjectTypes } from '@fi-app/shared/types/object-type';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgModel } from '@angular/forms';

@Component({
    selector: 'fi-onboarding-section-editor',
    templateUrl: './fi-onboarding-section-editor.component.html',
    styleUrls: ['./fi-onboarding-section-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-onboarding-section-editor' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiOnboardingSectionEditorComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiOnboardingSectionEditorComponent,
            multi: true,
        },
    ],
    animations: [Animations.fadeWidth(), Animations.fadeHeight()],
    standalone: false,
})
export class FiOnboardingSectionEditorComponent extends ControlValueAccessorValidatorBase<IOnboardingSection> {
    public isViewMode = input<boolean>();
    public sectionIndex = input.required<number>();
    public deleteSection = output<void>();

    private fiOnboardingBuilderHelperService = inject(FiOnboardingBuilderHelperService);
    private fiOnboardingElementDropListHelperService = inject(FiOnboardingElementDropListHelperService);
    private appTextService = inject(AppTextService);
    private fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private destroyRef = inject(DestroyRef);

    protected selectedLanguageId = computed<FiApplicationLanguageType>(() =>
        this.fiOnboardingBuilderStoreService.selectedLanguageId(),
    );
    protected isErrorShown = signal(false);
    protected isSectionInvalidStatusHighlighted = signal(false);
    protected isInputInvalid = computed<boolean>(() => {
        return (
            this.isErrorShown() &&
            (this.isSectionNameMaxLengthInvalid() || !!this.controlComponent?.errors?.['missing-name'])
        );
    });
    protected isErrorBannerShown = computed<boolean>(() => {
        return this.isSectionInvalidStatusHighlighted() && !!this.controlComponent?.errors?.['missing-steps'];
    });
    protected inputPlaceholder = computed<string>(() => {
        const placeholder = this.appTextService.get('onboarding.section_title');
        return this.isInputInvalid() ? `${placeholder}*` : placeholder;
    });

    protected sectionNameModelName = computed<string>(() => `section-name-${this.sectionIndex()}`);

    private isSectionNameMaxLengthInvalid = signal(false);
    private sectionNameValidationEffect = effect((onCleanup) => {
        const invalidModels = this.fiOnboardingBuilderHelperService.currentOnboardingInvalidModels();
        const sectionNameModel = invalidModels.find(
            (model: NgModel) => model.name === this.sectionNameModelName(),
        );
        const updateMaxLengthStatus = (): void => {
            this.isSectionNameMaxLengthInvalid.set(!!sectionNameModel?.errors?.['maxlength']);
        };

        updateMaxLengthStatus();
        const statusChangesSubscription = sectionNameModel?.statusChanges?.subscribe(updateMaxLengthStatus);
        onCleanup(() => statusChangesSubscription?.unsubscribe());
    });

    protected isSectionNameInputHintVisible = signal(false);

    protected readonly actions: DeepReadonly<IItemsListAction2<IOnboardingSection>[]> = [
        {
            title: this.appTextService.get('checklist.checkpoint_group_delete'),
            onClick: () => this.handleDeleteSectionClick(),
            iconClass: 'far fa-trash',
            optionType: FiItemsListOptionType.Icon,
        },
        {
            title: this.appTextService.get('onboarding.disable_section'),
            onClick: () => this.toggleSectionDisabledState(),
            iconClass: 'fas fa-eye-slash',
            optionType: FiItemsListOptionType.Icon,
            isChecked: () => this.ngModel.IsDisabled,
        },
    ];

    @ViewChild('sectionNameInputField', { read: ElementRef })
    private sectionNameInputEl: ElementRef<HTMLInputElement>;

    private _isControlStatusSubscribed = false;
    private _formSubmitEffect = effect(() => {
        const formSubmittedCount = this.fiOnboardingBuilderStoreService.formSubmitted();
        if (!formSubmittedCount) {
            return;
        }
        this.isErrorShown.set(true);
        this.subscribeToControlStatusChanges();
        this.onInvalidStatusUpdate();
    });

    protected focusSectionNameInput(): void {
        this.sectionNameInputEl?.nativeElement?.focus();
    }

    protected setStepElementId(objectId: number | undefined, objectType: ObjectTypes | undefined): string {
        if (!isDefined(objectId) || !isDefined(objectType)) {
            return '';
        }
        return this.fiOnboardingBuilderHelperService.getStepElementId(objectId, objectType);
    }

    private subscribeToControlStatusChanges(): void {
        if (this._isControlStatusSubscribed) {
            return;
        }
        this._isControlStatusSubscribed = true;
        this.controlComponent.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.onInvalidStatusUpdate();
        });
    }

    protected addStep(): void {
        this.ngModel.Steps.push(this.fiOnboardingBuilderHelperService.getNewOnboardingStepInstance());
        this.fiOnboardingElementDropListHelperService.addStepElementDropListId(
            this.sectionIndex(),
            this.ngModel.Steps.length - 1,
        );
        this.doPropagateChanges();
    }

    protected toggleSectionDisabledState(): void {
        this.ngModel.IsDisabled = !this.ngModel.IsDisabled;
        this.doPropagateChanges();
    }

    protected deleteStep(stepIndex: number): void {
        this.ngModel.Steps.splice(stepIndex, 1);
        this.fiOnboardingElementDropListHelperService.removeStepElementDropListId(this.sectionIndex(), stepIndex);
        this.doPropagateChanges();
    }

    protected disableStepChange(stepIndex: number, value: boolean): void {
        this.ngModel.Steps[stepIndex].IsDisabled = value;
        this.doPropagateChanges();
    }

    protected onStepDrop($event: CdkDragDrop<IOnboardingStep[]>): void {
        if ($event.previousContainer === $event.container) {
            moveItemInArray($event.container.data, $event.previousIndex, $event.currentIndex);
        }
    }

    private isSectionHasResponses(): boolean {
        return this.ngModel.Steps.some((step: IOnboardingStep) => step.HasResponses);
    }

    private handleDeleteSectionClick(): void {
        if (this.isSectionHasResponses()) {
            this.fiOnboardingBuilderHelperService
                .openConfirmDeleteActiveItemDialog(
                    this.appTextService.get('checklist.checkpoint_group_delete'),
                    this.appTextService.get('onboarding.confirm_delete_active_section_message'),
                )
                .onYesButtonClick((result: IFiOnboardingConfirmDeleteItemDialogResult) => {
                    if (result.isItemDeleted) {
                        this.deleteSection.emit();
                    } else if (result.isItemDisabled) {
                        this.ngModel.IsDisabled = true;
                        this.doPropagateChanges();
                    }
                });
            return;
        }
        if (!!this.ngModel.Steps?.length && this.ngModel.Steps.some((step: IOnboardingStep) => step.Element)) {
            this.fiOnboardingBuilderHelperService
                .openConfirmDeleteItemNotActiveDialog(
                    this.appTextService.get('checklist.checkpoint_group_delete'),
                    this.appTextService.get('onboarding.confirm_delete_not_active_section_message'),
                )
                .onYesButtonClick(() => this.deleteSection.emit());
            return;
        }
        this.deleteSection.emit();
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        if (!this.ngModel.IsDisabled) {
            return {
                'missing-steps': !this.ngModel.Steps?.length,
                'missing-active-steps': !this.ngModel.Steps?.filter((step: IOnboardingStep) => !step.IsDisabled).length,
                'missing-name': !this.ngModel.Names.some((name: IContentLanguageLabelModel) => name.Value),
            };
        }
    }

    private onInvalidStatusUpdate(): void {
        const errors = this.controlComponent.errors || {};
        this.isSectionInvalidStatusHighlighted.set(!!Object.keys(errors)?.length);
    }
}
